import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  type SxProps,
  type Theme,
} from "@mui/material";
import {
  ContentCopy as CopyIcon,
  ArrowBack as BackIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { useMemo, useState } from "react";
import { CSVLink } from "react-csv";
import { useNavigate, useParams } from "react-router-dom";
import { useApolloClient } from "@apollo/client";
import PageLayout from "../../components/common/page-layout";
import {
  ApiKeyAdminExportCsvDocument,
  type ApiKeyAdminExportCsvQuery,
  type ApiKeyAdminExportCsvQueryVariables,
  ApiKeyAdminGetDocument,
  ApiKeyAdminListDocument,
  useApiKeyAdminDeleteMutation,
  useApiKeyAdminDisconnectOrgMutation,
  useApiKeyAdminGetQuery,
} from "../../generated/graphql";
import ConnectOrgsDialog from "./components/connect-orgs-dialog";
import EditApiKeyDialog from "./components/edit-api-key-dialog";
import { useConfirmDialog } from "./components/confirm-dialog";
import { CUSTOMER_CSV_HEADERS, toCustomerCsvRows } from "./customer-csv";
import useIsInternalAdmin from "./hooks/use-is-internal-admin";
import { throwOnGraphqlErrors } from "./throw-on-graphql-errors";

const styles = {
  page: { p: 3 } satisfies SxProps<Theme>,
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    mb: 2,
  } satisfies SxProps<Theme>,
  scalars: {
    display: "grid",
    gridTemplateColumns: "200px 1fr",
    rowGap: 1,
    columnGap: 2,
    mb: 3,
  } satisfies SxProps<Theme>,
  monospace: { fontFamily: "monospace" } satisfies SxProps<Theme>,
  stagingChip: { mb: 2 } satisfies SxProps<Theme>,
  centered: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    p: 4,
  } satisfies SxProps<Theme>,
};

const csvLinkStyle = { textDecoration: "none" } as const;

const slugify = (input: string): string => {
  const slug = input
    .toLowerCase()
    .replaceAll(/[^a-z0-9]+/g, "-")
    .replaceAll(/^-+|-+$/g, "");
  return slug.length === 0 ? "api-key" : slug;
};

const formatDate = (date: Date): string => {
  const y = date.getFullYear().toString().padStart(4, "0");
  const m = (date.getMonth() + 1).toString().padStart(2, "0");
  const d = date.getDate().toString().padStart(2, "0");
  return `${y}${m}${d}`;
};

/**
 * Detail page for a single API key. Shows metadata, linked organizations,
 * and action buttons for: add orgs, disconnect an org, edit scalars,
 * delete the key, download the full internal CSV, and export a secondary
 * customer-facing CSV (company + billing-contact columns only).
 */
const ApiKeyAdminDetailPage = () => {
  const { keyUuid } = useParams<{ keyUuid: string }>();
  const navigate = useNavigate();
  const apolloClient = useApolloClient();
  const { confirm, confirmDialog } = useConfirmDialog();
  const { canAccess, loading: permissionsLoading } = useIsInternalAdmin();

  const [editOpen, setEditOpen] = useState(false);
  const [connectOpen, setConnectOpen] = useState(false);
  const [orgFilter, setOrgFilter] = useState("");
  const [pageError, setPageError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const { data, loading, error } = useApiKeyAdminGetQuery({
    variables: { uuid: keyUuid ?? "" },
    skip: !canAccess || keyUuid === undefined,
    fetchPolicy: "cache-and-network",
  });

  const [disconnectOrg, { loading: disconnecting }] =
    useApiKeyAdminDisconnectOrgMutation({
      refetchQueries: [ApiKeyAdminGetDocument, ApiKeyAdminListDocument],
    });

  const [deleteKey] = useApiKeyAdminDeleteMutation({
    refetchQueries: [ApiKeyAdminListDocument],
  });

  const detail = data?.apiKeyAdminGet;

  const filteredOrgs = useMemo(() => {
    const orgs = detail?.linkedOrgs ?? [];
    const query = orgFilter.trim().toLowerCase();
    if (query === "") {
      return orgs;
    }
    return orgs.filter(
      (o) =>
        o.companyName.toLowerCase().includes(query) ||
        o.organizationDisplayName.toLowerCase().includes(query),
    );
  }, [detail, orgFilter]);

  const alreadyLinkedUuids = useMemo(
    () => new Set((detail?.linkedOrgs ?? []).map((o) => o.organizationUuid)),
    [detail],
  );

  const customerCsvRows = useMemo(
    () => toCustomerCsvRows(detail?.linkedOrgs ?? []),
    [detail],
  );

  const customerCsvFilename = useMemo(() => {
    const namePart = slugify(detail?.apiKey.name ?? "api-key");
    const today = formatDate(new Date());
    const hostname = slugify(globalThis.location.hostname);
    return `${namePart}-customer-${hostname}-${today}.csv`;
  }, [detail]);

  const flashNotice = (message: string) => {
    setNotice(message);
    globalThis.setTimeout(() => {
      setNotice(null);
    }, 1500);
  };

  const handleCopy = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value);
      flashNotice(`${label} copied`);
    } catch {
      setPageError("Copy to clipboard failed");
    }
  };

  const handleDisconnect = async (
    organizationUuid: string,
    organizationDisplayName: string,
  ) => {
    if (detail === undefined) return;
    try {
      await confirm({
        title: "Disconnect organization?",
        description: `Remove "${organizationDisplayName}" from the "${detail.apiKey.name}" key?`,
        confirmationText: "Disconnect",
      });
    } catch {
      return;
    }
    try {
      const result = await disconnectOrg({
        variables: {
          input: { apiKeyUuid: detail.apiKey.uuid, organizationUuid },
        },
      });
      throwOnGraphqlErrors(result);
    } catch (error_) {
      const message =
        error_ instanceof Error ? error_.message : "Unknown error";
      setPageError(message);
    }
  };

  const handleDelete = async () => {
    if (detail === undefined) return;
    const { name } = detail.apiKey;
    try {
      await confirm({
        title: "Delete API key?",
        description: `Type the key name (${name}) to confirm deletion. This cannot be undone.`,
        confirmationKeyword: name,
        confirmationKeywordPlaceholder: name,
        confirmationText: "Delete",
      });
    } catch {
      return;
    }
    try {
      const result = await deleteKey({
        variables: { uuid: detail.apiKey.uuid },
      });
      throwOnGraphqlErrors(result);
      navigate("/admin/api-keys");
    } catch (error_) {
      const message =
        error_ instanceof Error ? error_.message : "Unknown error";
      setPageError(message);
    }
  };

  const handleDownloadCsv = async () => {
    if (detail === undefined) return;
    try {
      await confirm({
        title: "Download CSV",
        description:
          "The file may contain a webhook auth header value. Store it securely.",
        confirmationText: "Download",
      });
    } catch {
      return;
    }
    try {
      const result = await apolloClient.query<
        ApiKeyAdminExportCsvQuery,
        ApiKeyAdminExportCsvQueryVariables
      >({
        query: ApiKeyAdminExportCsvDocument,
        variables: { uuid: detail.apiKey.uuid },
        fetchPolicy: "no-cache",
      });
      const csv = result.data.apiKeyAdminExportCsv;
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      const today = formatDate(new Date());
      const hostname = slugify(globalThis.location.hostname);
      link.href = url;
      link.download = `${slugify(detail.apiKey.name)}-${hostname}-${today}.csv`;
      document.body.append(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error_) {
      const message =
        error_ instanceof Error ? error_.message : "Unknown error";
      setPageError(message);
    }
  };

  if (permissionsLoading || (loading && detail === undefined)) {
    return (
      <PageLayout maxWidth={false} title="API key">
        <Box sx={styles.centered}>
          <CircularProgress />
        </Box>
      </PageLayout>
    );
  }
  if (!canAccess) {
    return (
      <PageLayout maxWidth={false} title="API key">
        <Alert severity="error">
          This tool requires the INTERNAL_TOOLS permission.
        </Alert>
      </PageLayout>
    );
  }
  if (error !== undefined) {
    return (
      <PageLayout maxWidth={false} title="API key">
        <Alert severity="error">{error.message}</Alert>
      </PageLayout>
    );
  }
  if (detail === undefined) {
    return (
      <PageLayout maxWidth={false} title="API key">
        <Alert severity="warning">API key not found.</Alert>
      </PageLayout>
    );
  }

  const { apiKey, linkedOrgs } = detail;

  return (
    <PageLayout
      maxWidth={false}
      title={apiKey.name}
      actions={
        <Button
          size="small"
          startIcon={<BackIcon />}
          onClick={() => {
            navigate("/admin/api-keys");
          }}
        >
          All API keys
        </Button>
      }
    >
      {apiKey.enableStaging && (
        <Chip
          size="small"
          label="staging enabled"
          color="info"
          sx={styles.stagingChip}
        />
      )}
      {pageError !== null && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {pageError}
        </Alert>
      )}
      {notice !== null && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {notice}
        </Alert>
      )}
      <Box sx={styles.headerRow}>
        <Stack useFlexGap direction="row" spacing={1} flexWrap="wrap">
          <Button
            variant="contained"
            onClick={() => {
              setConnectOpen(true);
            }}
          >
            Add organizations
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              void handleDownloadCsv();
            }}
          >
            Download CSV
          </Button>
          <CSVLink
            data={customerCsvRows}
            headers={CUSTOMER_CSV_HEADERS}
            filename={customerCsvFilename}
            style={csvLinkStyle}
          >
            <Button variant="outlined">Customer CSV</Button>
          </CSVLink>
          <Button
            variant="outlined"
            onClick={() => {
              setEditOpen(true);
            }}
          >
            Edit
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              navigate(`/admin/api-keys/${apiKey.uuid}/payload-mapping`);
            }}
          >
            Payload mappings
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={() => {
              void handleDelete();
            }}
          >
            Delete
          </Button>
        </Stack>
      </Box>
      <Box sx={styles.scalars}>
        <Typography variant="body2" color="text.secondary">
          UUID
        </Typography>
        <Typography sx={styles.monospace}>
          {apiKey.uuid}
          <IconButton
            size="small"
            onClick={() => {
              void handleCopy(apiKey.uuid, "UUID");
            }}
          >
            <CopyIcon fontSize="inherit" />
          </IconButton>
        </Typography>
        <Typography variant="body2" color="text.secondary">
          API Key
        </Typography>
        <Typography sx={styles.monospace}>
          {apiKey.apiKey}
          <IconButton
            size="small"
            onClick={() => {
              void handleCopy(apiKey.apiKey, "API key");
            }}
          >
            <CopyIcon fontSize="inherit" />
          </IconButton>
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Webhook URL
        </Typography>
        <Typography>{apiKey.webhookUrl ?? "—"}</Typography>
        <Typography variant="body2" color="text.secondary">
          Header name
        </Typography>
        <Typography>{apiKey.webhookAuthHeaderName ?? "—"}</Typography>
        <Typography variant="body2" color="text.secondary">
          Header value
        </Typography>
        <Typography>
          {apiKey.webhookAuthHeaderValue === null ||
          apiKey.webhookAuthHeaderValue === undefined
            ? "—"
            : "•••••• (set)"}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Created
        </Typography>
        <Typography>
          {new Date(String(apiKey.createdAt)).toLocaleString()}
        </Typography>
      </Box>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Linked organizations ({linkedOrgs.length})
      </Typography>
      <TextField
        fullWidth
        size="small"
        placeholder="Filter linked orgs by company or organization name"
        value={orgFilter}
        sx={{ mb: 2 }}
        onChange={(e) => {
          setOrgFilter(e.target.value);
        }}
      />
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Company</TableCell>
              <TableCell>Company UUID</TableCell>
              <TableCell>Organization</TableCell>
              <TableCell>Organization UUID</TableCell>
              <TableCell align="right">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredOrgs.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No linked organizations match.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {filteredOrgs.map((org) => (
              <TableRow key={org.organizationUuid}>
                <TableCell>{org.companyName}</TableCell>
                <TableCell>
                  <Typography variant="caption" fontFamily="monospace">
                    {org.companyUuid}
                  </Typography>
                </TableCell>
                <TableCell>{org.organizationDisplayName}</TableCell>
                <TableCell>
                  <Typography variant="caption" fontFamily="monospace">
                    {org.organizationUuid}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Disconnect">
                    <IconButton
                      size="small"
                      color="error"
                      disabled={disconnecting}
                      onClick={() => {
                        void handleDisconnect(
                          org.organizationUuid,
                          org.organizationDisplayName,
                        );
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <EditApiKeyDialog
        open={editOpen}
        apiKey={apiKey}
        onClose={() => {
          setEditOpen(false);
        }}
      />
      <ConnectOrgsDialog
        open={connectOpen}
        apiKeyUuid={apiKey.uuid}
        alreadyLinkedOrgUuids={alreadyLinkedUuids}
        onClose={() => {
          setConnectOpen(false);
        }}
      />
      {confirmDialog}
    </PageLayout>
  );
};

export default ApiKeyAdminDetailPage;
